import Immutable from "seamless-immutable";
import head from "lodash.head";
import map from "lodash.map";
import { App } from "../stores";
import {
  UserActions,
  AppActions,
  ProjectActions,
  BudgetActions,
} from "../actions";
import {
  DefaultOffice,
  DistrictDropdownOption,
  ProjectType,
  ExpendActionType,
} from "../utils/enums";

const initialState = Immutable(App);
const SuperAdminPermission = -1;

export function app(state = initialState, action) {
  switch (action.type) {
    case UserActions.InitialLoginUserAction.Request:
    case UserActions.LoginUserAction.Request: {
      return state.merge({
        isLoging: true,
      });
    }
    case UserActions.InitialLoginUserAction.Success:
    case UserActions.InitialLoginUserAction.Failure:
    case UserActions.LoginUserAction.Success:
    case UserActions.LoginUserAction.Failure: {
      let layoutPermissions = action.payload.officePermissions.reduce(
        (permissions, officePermissionCurrentValue) => {
          officePermissionCurrentValue.permissions.reduce(
            (prevValue, permissionsCurrentValue) => {
              if (permissions.indexOf(permissionsCurrentValue.id) === -1) {
                permissions.push(permissionsCurrentValue.id);
              }
            },
            permissions
          );
          return permissions;
        },
        []
      );

      if (
        action.payload.isAdmin &&
        layoutPermissions.indexOf(SuperAdminPermission) === -1
      ) {
        layoutPermissions.push(SuperAdminPermission);
      }

      return state.merge({
        isAuth: action.payload.isAuth,
        layoutPermissions: layoutPermissions,
        loggedUser: {
          isAdmin: action.payload.isAdmin,
          isSysAdmin: action.payload.isSysAdmin,
          loginName: action.payload.loginName,
          permissions: action.payload.officePermissions,
          userId: action.payload.userId,
          officeId: action.payload.officeId,
          districtId: action.payload.districtId,
          aswAccountId: action.payload.aswAccountId,
          aswMainProfileId: action.payload.aswMainProfileId,
          isCitizen: action.payload.isCitizen,
        },
        isLoging: false,
      });
    }
    case UserActions.LogoutUserAction.Success: {
      return state.merge({
        isAuth: false,
        layoutPermissions: [],
        loggedUser: {
          isAdmin: false,
          isSysAdmin: false,
          loginName: "",
          permissions: [],
          userId: null,
          officeId: null,
          districtId: DistrictDropdownOption.NotSetted,
        },
      });
    }
    case AppActions.GetDefaultSmtpConfigurationAction.Success: {
      return state.merge({
        smtp: action.payload,
      });
    }
    case AppActions.FetchPermissionsAction.Success: {
      return state.merge({
        permissions: action.payload,
      });
    }
    case AppActions.FetchRolesAction.Success: {
      return state.merge({
        roles: action.payload,
      });
    }
    case AppActions.GetInitDataAction.Success: {
      const defaultOffice =
        action.payload.availableOffices &&
        action.payload.availableOffices.length > 0
          ? head(action.payload.availableOffices)
          : { id: DefaultOffice.NotSetted };

      return state.merge({
        permissions: action.payload.availablePermissions,
        roles: action.payload.availableRoles,
        offices: action.payload.availableOffices,
        defaultOffice: defaultOffice,
        officeThematicAreas: action.payload.availableOfficeThematicAreas,
        thematicAreas: action.payload.availableThematicAreas,
        projectModes: action.payload.availableProjectModes,
        commentingModes: action.payload.availableCommentingModes,
        projectStatuses: action.payload.availableProjectStatuses,
        projectTypes: action.payload.availableProjectTypes,
        moderators: action.payload.availableModerators,
        agreementStatuses: action.payload.availableAgreementStatuses,
        commentStatuses: action.payload.availableCommentStatuses,
        publishProgress: action.payload.availablePublishProgress,
        voteProgress: action.payload.availableVoteProgress,
        externalSystemAddress: action.payload.externalSystemAddress,
        officeFooter: action.payload.officeFooter,
        attachmentConfiguration: action.payload.attachmentConfiguration,
        initiativeConfiguration: action.payload.initiativeConfiguration,
        districts: action.payload.districts,
        availableBudgetYears: action.payload.availableBudgetYears,
        mapConfiguration: action.payload.mapConfiguration,
        reCaptchaConfiguration: action.payload.reCaptchaConfiguration,
        projectTaskStatuses: action.payload.projectTaskStatuses,
        appVersion: action.payload.appVersion,
        appModule: action.payload.appModule || {
          consultation: false,
          budget: false,
          probe: false,
          survey: false,
        },
        appModuleFetched: true,
        legalDocument: action.payload.legalDocument,
        helpPageData: action.payload.helpPageData,
        streetFeatures: action.payload.streetFeatures,
        systemParameters: action.payload.systemParameters,
        serverIdentity: action.payload.serverIdentity,
        topMenuElements: action.payload.topMenuElements,
        tiles: action.payload.tiles,
        eformExternalServiceAddress: action.payload.eformExternalServiceAddress,
        accountEditWebComponentAddress:
          action.payload.accountEditWebComponentAddress,
        aswApi: action.payload.aswApi,
        aswWeb: action.payload.aswWeb,
      });
    }
    case AppActions.SearchProjectAction.Request: {
      return state.merge({
        searchList: [],
        totalSearchListItems: 0,
        isSearching: true,
      });
    }
    case AppActions.SearchProjectAction.Success: {
      return state.merge({
        searchList: action.payload.items,
        totalSearchListItems: action.payload.totalItems,
        isSearching: false,
      });
    }
    case AppActions.SearchProjectAction.Failure: {
      return state.merge({
        isSearching: false,
      });
    }
    case AppActions.GetMainPageProjectsAction.Success: {
      return state.merge({
        mainPageProjects: action.payload,
      });
    }
    case AppActions.GetTermsOfUseAction.Success: {
      return state.merge({
        termsOfUse: action.payload,
      });
    }
    case AppActions.UnloadAppAction.Success: {
      return state.merge({
        termsOfUse: null,
        searchList: [],
        totalSearchListItems: 0,
      });
    }
    case AppActions.SetPageSizeAction.Success: {
      return state.merge({
        pageSize: action.payload,
      });
    }

    case AppActions.GetApplicationImagesAction.Success: {
      return state.merge({
        applicationImages: action.payload,
      });
    }
    case AppActions.SetArchivePageSizeAction.Success: {
      return state.merge({
        archivePageSize: action.payload,
      });
    }
    case AppActions.GetMainPageProbeProjectsAction.Success: {
      return state.merge({
        mainPageProbeProjects: action.payload,
      });
    }
    case ProjectActions.UpdateConsultationFormAction.Success: {
      const { project } = action.payload;
      if (project.type === ProjectType.Probe) {
        let projectIndex = state.mainPageProbeProjects.findIndex(
          (item) => item.id === project.id
        );
        let projects = state.getIn(["mainPageProbeProjects"]);

        return state.merge({
          mainPageProbeProjects: projects
            .slice(0, projectIndex)
            .concat(project)
            .concat(projects.slice(projectIndex + 1)),
        });
      }

      return state;
    }
    case AppActions.SetExpandStateItemAction.Success: {
      const { action: addOrRemove, property, id } = action.payload;
      if (addOrRemove === ExpendActionType.add) {
        let expandItems = state.getIn(["expand", property]);
        if (!expandItems) {
          expandItems = [];
        }
        expandItems = expandItems.concat(id);

        return state.setIn(["expand", property], expandItems);
      }

      if (addOrRemove === ExpendActionType.remove) {
        let expandItems = state.getIn(["expand", property]);
        let expandItemIndex = expandItems.findIndex((item) => item === id);

        if (expandItemIndex !== -1) {
          expandItems = expandItems
            .slice(0, expandItemIndex)
            .concat(expandItems.slice(expandItemIndex + 1));
        }

        return state.setIn(["expand", property], expandItems);
      }

      return state;
    }
    case BudgetActions.GetAllBudgetsAction.Success: {
      let budgetIds = map(action.payload.items, (item) => item.id);

      return state.setIn(["expand", "budgets"], budgetIds);
    }
    case AppActions.SetBreadcrumbsAction.Success: {
      return state.setIn(["breadcrumbs"], action.payload);
    }
    default: {
      return state;
    }
  }
}
